import { useMemo, useState, useRef } from "react";
import { toast } from "react-toastify";

import Button from "components/v1/Button";

// Assets
import IDot from "assets/i-dot.js";
import IPlane from "assets/i-plane.js";
import ISettings from "assets/i-settings.js";
import IPin from "assets/i-pin.js";
import IInfo from "assets/i-info.js";
import IWarning from "assets/i-warning.js";
import IThump from "assets/i-thump.js";

import ModalDetailListing from "components/molecules/buyer-match/modal-detail-listing";
import ModalKonfirmasi from "components/molecules/buyer-match/modal-konfirmasi/modal-konfirmasi";

import useOutsideAlerter from "hooks/useOutsideAlerter";
import messageAgent from "helpers/messageAgent";
import { useGlobalCtx } from "contexts";
import { useUpdateStatusListing } from "api/buyer-match/mutation";
import { usePostPropertyShare } from "api/common/mutation";

const ButtonThump = ({ isActive, isDown, ...props }) => {
  return (
    <button
      {...props}
      className={`w-4 h-4 rounded-[4px] flex items-center justify-center ${
        isActive ? "bg-white" : ""
      } ${isDown ? "rotate-180" : ""}`}
    >
      <IThump color={!isActive ? "#fff" : "#252526"} />
    </button>
  );
};

const FEAT = ({ val, lastItem }) => {
  const _getQubic = () => {
    if (val.includes("LT") || val.includes("LB"))
      return (
        <span>
          m<sup>2</sup>
        </span>
      );
    return "";
  };
  return (
    <>
      <span className="text-[11px] tracking-[-0.4px] leading-[18.571px] px-2 py-0.5 bg-white rounded-[4px] font-inter text-[#808285]">
        {val} {_getQubic()}
      </span>
      {!lastItem && <IDot />}
    </>
  );
};

const ButtonSettings = ({ onOpenModalDetail, onOpenModalNotInterested }) => {
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);
  const ref = useRef(null);

  const handleCloseDropdown = () => {
    setIsOpenDropdown(false);
  };

  const handleToggleDropdown = () => {
    setIsOpenDropdown(!isOpenDropdown);
  };

  useOutsideAlerter(ref, handleCloseDropdown);

  return (
    <div ref={ref} className="relative">
      <Button
        icon={ISettings}
        type="secondary"
        iconColor="#252526"
        className="!p-[10px]"
        size="s"
        onClick={handleToggleDropdown}
      />

      {isOpenDropdown && (
        <div className="absolute right-0 top-full mt-1 w-[200px] bg-white rounded-[8px] z-10 border-[0.5px] border-solid border-[#D8D8D9]">
          <button
            className="w-full p-2 text-left text-[13px] font-inter text-[#252526] flex items-center rounded-t-[4px] border-[0px] gap-1 !border-b-[0.5px] !border-solid !border-[#D8D8D9]"
            onClick={() => {
              onOpenModalDetail();
              setIsOpenDropdown(false);
            }}
          >
            <div>
              <IInfo width="16" height="16" />
            </div>
            Detail Listing
          </button>
          <button
            className="w-full p-2 text-left text-[13px] font-inter text-[#FD4949] flex items-center rounded-b-[4px] border-[0px] gap-1"
            onClick={() => {
              onOpenModalNotInterested();
              setIsOpenDropdown(false);
            }}
          >
            <div>
              <IWarning />
            </div>
            Customer Tidak Tertarik
          </button>
        </div>
      )}
    </div>
  );
};

const CardListingDetailBuyer = ({
  customer,
  user,
  property_locations,
  property,
  status,
  property_images,
  id,
  refetchListListingCustomer,
}) => {
  const { listPropertyCategories } = useGlobalCtx();
  const { mutate: updateStatusListing } = useUpdateStatusListing();
  const { mutate: postPropertyShare } = usePostPropertyShare();

  const [isOpenModal, setIsOpenModal] = useState("kirim"); // kirim, detail, konfirmasi, konfirmasi-reject
  const category = listPropertyCategories?.find(
    (item) => item.id === property?.property_category_id
  );

  const openThumb = status === "not_interested" || status === "interested";

  const openThumbSent = status === "sent";

  const handleCloseModal = () => {
    setIsOpenModal("");
  };

  const DATA_FEATURE = useMemo(() => {
    if (!property) return [];

    const temp = [];

    if (property?.bedroom) temp.push(`Kamar Tidur: ${property?.bedroom}`);
    if (property?.bathroom) temp.push(`Kamar Mandi: ${property?.bathroom}`);
    if (property?.surface_area)
      temp.push(`Luas Tanah: ${property?.surface_area}`);
    if (property?.building_area)
      temp.push(`Luas Bangunan: ${property?.building_area}`);

    return temp;
  }, [property]);

  const handleKirimListing = (referralLink) => {
    const customerPhone = customer?.whatsapp_number;

    const linkProperty = referralLink;
    const text = `${property?.property_type_id === 1 ? "Dijual" : "Disewa"} ${
      category?.name
    }\n\n${property?.title}\n${property_locations?.street ?? "-"}\n\n${
      property?.surface_area
        ? "Luas Tanah: " + property?.surface_area + "m2"
        : ""
    }\n${
      property?.building_area
        ? "Luas Bangunan: " + property?.building_area + "m2"
        : ""
    }\n${property?.bedroom ? "Kamar Tidur: " + property?.bedroom : ""}\n${
      property?.bathroom ? "Kamar Mandi: " + property?.bathroom : ""
    }\n${
      parseInt(property?.price)
        ? "Rp" +
          parseInt(property?.price).toLocaleString("ID-id") +
          property?.unit_price?.name
        : ""
    }\n\nUntuk detail lebih lanjut, Silahkan klik link di bawah ini:\n${linkProperty}\n\nTerima kasih\n\n${
      user?.full_name
    }\n\nCENTURY 21 ${user?.company_name}`;

    messageAgent(customerPhone, text);
  };

  const handleCustomerTidakTertarik = (status) => {
    const data = {
      listing_customer: {
        status, // "matched", "sent", "interested", "not_interested"
      },
    };
    updateStatusListing(
      {
        id,
        data,
      },
      {
        onSuccess: (responseData) => {
          setIsOpenModal("");
          refetchListListingCustomer();
          status === "sent" &&
            handleKirimListing(responseData?.data?.data?.referral_link);
          toast.success("Berhasil update listing status");
        },
        onError: (error) => {
          toast.error(error?.message ?? "Gagal update listing status");
        },
      }
    );
  };

  const handlePostPropertyShare = (status) => {
    const data = {
      property_share: {
        is_whatsapp: true,
      },
    };

    postPropertyShare(
      {
        slug: property?.slug,
        data,
      },
      {
        onSuccess: (responseData) => {
          setIsOpenModal("");
          refetchListListingCustomer();
          handleKirimListing(responseData?.data?.data?.referral_link);
          toast.success("Berhasil share listing ke WhatsApp customer");
        },
        onError: (error) => {
          toast.error(
            error?.message ?? "Gagal share listing ke WhatsApp customer"
          );
        },
      }
    );
  };

  return (
    <div className="py-[12px] px-4 flex items-center gap-[12px] border-b-[0.5px] border-solid border-[#D8D8D9] last:border-b-0">
      <div className="w-[64px] aspect-square rounded-[8px]">
        <img
          src={property_images?.[0]?.image?.url}
          alt={property?.title ?? "Property"}
          className="w-full h-full object-cover rounded-[8px]"
        />
      </div>
      <div className="flex flex-col justify-between items-start w-full">
        <div className="flex justify-between gap-[4px] w-full">
          <p className="flex items-center gap-1 text-[13px] tracking-[-0.4px] leading-[21px] font-inter text-[#252526]">
            <IPin /> {property_locations?.street}
          </p>

          <div className="w-[350px] flex items-center gap-2 justify-end">
            {openThumb ? (
              <div
                className={`h-fit flex items-center gap-1 p-0.5 rounded-[5px] ${
                  status === "not_interested" ? "bg-[#FD4949]" : "bg-[#02AC5B]"
                }`}
              >
                <ButtonThump
                  onClick={() => setIsOpenModal("konfirmasi-change-status")}
                  isActive={status === "interested"}
                />

                <ButtonThump
                  onClick={() => setIsOpenModal("konfirmasi-change-status")}
                  isActive={status === "not_interested"}
                  isDown={true}
                />
              </div>
            ) : openThumbSent ? (
              <div
                className={`flex h-fit items-center gap-1 p-0.5 rounded-[5px] `}
              >
                <Button
                  type="secondary"
                  icon={IThump}
                  onClick={() => handleCustomerTidakTertarik("interested")}
                  size="xs"
                  text="Tertarik"
                  iconPosition="right"
                />
                <Button
                  type="secondary"
                  icon={IThump}
                  onClick={() => handleCustomerTidakTertarik("not_interested")}
                  size="xs"
                  text="Tidak Tertarik"
                  iconPosition="right"
                  rotateIcon={"180"}
                />
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <Button
                  icon={IPlane}
                  className="!p-[10px]"
                  iconPosition="right"
                  text="Kirim"
                  size="s"
                  onClick={() => setIsOpenModal("konfirmasi")}
                />

                <ButtonSettings
                  onOpenModalNotInterested={() =>
                    setIsOpenModal("konfirmasi-reject")
                  }
                  onOpenModalDetail={() => {
                    setIsOpenModal("detail");
                  }}
                />
              </div>
            )}

            {status !== "matched" && (
              <Button
                type="secondary"
                icon={IPlane}
                onClick={handlePostPropertyShare}
                size="xs"
                text="Kirim Ulang"
                iconPosition="right"
                iconColor="#252526"
              />
            )}
          </div>
        </div>

        <div className="flex gap-[10px] justify-between w-full">
          <div>
            <p className="text-[13px] tracking-[-0.4px] leading-[21px] font-inter text-[#252526]">
              Rp{parseInt(property?.price).toLocaleString()}{" "}
              {property?.unit_price?.name?.replace("/", "Per")}
            </p>
            <p className="text-[11px] tracking-[-0.4px] leading-[18.571px] font-inter text-[#808285]">
              {property?.title}
            </p>
          </div>
          <div className="flex items-center justify-end gap-1 rounded-[2px] bg-[#FAFAFA] px-1 py-2">
            {DATA_FEATURE?.map((item, index) => (
              <FEAT
                key={`item-data-faeture-${index}`}
                val={item}
                lastItem={index === DATA_FEATURE?.length - 1}
              />
            ))}
          </div>
        </div>

        <p className="text-[11px] tracking-[-0.4px] leading-[18.571px] w-full font-inter text-[#808285] border-t-[0.5px] border-dashed border-[#DEDFDE]">
          Dibuat{" "}
          <span className="font-semibold">
            {user?.full_name}, {user?.company_name}
          </span>
        </p>
      </div>

      <ModalDetailListing
        isOpen={isOpenModal === "detail"}
        onClose={() => setIsOpenModal("")}
        slug={property?.slug}
        onSendListing={() => setIsOpenModal("konfirmasi")}
        property_images={property_images}
        propertyId={id}
      />

      <ModalKonfirmasi
        isOpen={isOpenModal === "konfirmasi"}
        onClose={handleCloseModal}
        title="Kirim Listing ke Customer?"
        description="Listing ini akan dibagikan kepada customer melalui WhatsApp."
        leftBtnText="Batal"
        rightBtnText="Kirim Listing"
        leftBtnAction={handleCloseModal}
        rightBtnAction={() => handleCustomerTidakTertarik("sent")}
      />

      <ModalKonfirmasi
        isOpen={isOpenModal === "konfirmasi-reject"}
        onClose={handleCloseModal}
        title="Tandai Listing Tidak Diminati?"
        description="Listing ini akan dihapus dari profil customer karena dianggap tidak diminati."
        leftBtnText="Batal"
        rightBtnText="Ya, Tandai"
        leftBtnAction={handleCloseModal}
        rightBtnAction={() => handleCustomerTidakTertarik("not_interested")}
      />

      <ModalKonfirmasi
        isOpen={isOpenModal === "konfirmasi-change-status"}
        onClose={handleCloseModal}
        title="Ubah Respon Customer?"
        description="Respon customer akan diubah menjadi Tertarik untuk listing ini."
        leftBtnText="Batal"
        rightBtnText="Ubah Respon"
        leftBtnAction={handleCloseModal}
        rightBtnAction={() =>
          handleCustomerTidakTertarik(
            status === "not_interested" ? "interested" : "not_interested"
          )
        }
      />
    </div>
  );
};

export default CardListingDetailBuyer;
